import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  FolderSimple, FolderSimplePlus, File, FileText, FileImage, FilePdf,
  UploadSimple, Trash, PencilSimple, ArrowLeft, DotsThree,
  MagnifyingGlass, Link as LinkIcon, BookmarkSimple, DownloadSimple,
  GridFour, ListBullets, Plus, CaretRight, House, ClipboardText, Copy
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { generateBibliography, CITATION_STYLES } from '../../utils/citations';

const FILE_ICONS = {
  'application/pdf': FilePdf,
  'image/png': FileImage, 'image/jpeg': FileImage, 'image/gif': FileImage, 'image/webp': FileImage,
  'text/plain': FileText, 'text/csv': FileText,
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function MyFilesPage() {
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderData, setFolderData] = useState(null);
  const [rootFolders, setRootFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showBibliography, setShowBibliography] = useState(false);
  const [bibStyle, setBibStyle] = useState('apa');

  const loadFolder = useCallback(async () => {
    setLoading(true);
    try {
      if (currentFolderId) {
        const data = await api.getFolder(currentFolderId);
        setFolderData(data);
      } else {
        const folders = await api.getFolders(null);
        setRootFolders(folders);
        setFolderData(null);
      }
    } catch (err) {
      toast.error('Failed to load folder');
    }
    setLoading(false);
  }, [currentFolderId]);

  useEffect(() => { loadFolder(); }, [loadFolder]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.createFolder(newFolderName.trim(), currentFolderId);
      toast.success('Folder created');
      setNewFolderName('');
      setShowNewFolder(false);
      loadFolder();
    } catch (err) {
      toast.error('Failed to create folder');
    }
  };

  const handleRename = async () => {
    if (!renameName.trim() || !renameTarget) return;
    try {
      await api.updateFolder(renameTarget.id, renameName.trim());
      toast.success('Folder renamed');
      setRenameTarget(null);
      setRenameName('');
      loadFolder();
    } catch (err) {
      toast.error('Failed to rename');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder and all its contents?')) return;
    try {
      await api.deleteFolder(folderId);
      toast.success('Folder deleted');
      loadFolder();
    } catch (err) {
      toast.error('Failed to delete folder');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.deleteFolderItem(itemId);
      toast.success('Item deleted');
      loadFolder();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !currentFolderId) return;
    setUploading(true);
    let success = 0;
    for (const file of files) {
      try {
        await api.uploadFileToFolder(currentFolderId, file);
        success++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (success > 0) toast.success(`Uploaded ${success} file(s)`);
    setUploading(false);
    fileInputRef.current.value = '';
    loadFolder();
  };

  const openFolder = (folderId) => setCurrentFolderId(folderId);
  const goBack = () => {
    if (folderData?.parent_id) setCurrentFolderId(folderData.parent_id);
    else setCurrentFolderId(null);
  };

  const breadcrumbs = folderData?.breadcrumbs || [];
  const folders = currentFolderId ? (folderData?.subfolders || []) : rootFolders;
  const items = folderData?.items || [];
  const filtered = searchQuery
    ? [...folders.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase())),
       ...items.filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()))]
    : null;

  const getItemIcon = (item) => {
    if (item.item_type === 'bookmark') return BookmarkSimple;
    if (item.item_type === 'link') return LinkIcon;
    return FILE_ICONS[item.mime_type] || File;
  };

  const handleDownload = (itemId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/folder-items/${itemId}/download`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="my-files-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            My Files
          </h1>
          <p className="text-sm text-slate-400 mt-1">Organize your documents, bookmarks, and lab resources</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')} data-testid="view-toggle">
            {viewMode === 'grid' ? <ListBullets size={16} /> : <GridFour size={16} />}
          </Button>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setShowNewFolder(true)} data-testid="new-folder-btn">
            <FolderSimplePlus size={16} className="mr-1" /> New Folder
          </Button>
          {currentFolderId && (
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-btn">
              <UploadSimple size={16} className="mr-1" /> {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
          {currentFolderId && items.some(i => i.item_type === 'bookmark') && (
            <Button size="sm" variant="outline" onClick={() => setShowBibliography(true)} data-testid="bibliography-btn">
              <ClipboardText size={16} className="mr-1" /> Bibliography
            </Button>
          )}
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileUpload} data-testid="file-input" />
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 text-sm" data-testid="breadcrumbs">
        <button onClick={() => setCurrentFolderId(null)} className="flex items-center gap-1 text-slate-400 hover:text-indigo-400 transition-colors">
          <House size={14} /> My Files
        </button>
        {breadcrumbs.map((bc) => (
          <React.Fragment key={bc.id}>
            <CaretRight size={12} className="text-slate-400" />
            <button onClick={() => setCurrentFolderId(bc.id)} className="text-slate-400 hover:text-indigo-400 transition-colors">{bc.name}</button>
          </React.Fragment>
        ))}
        {folderData && (
          <>
            <CaretRight size={12} className="text-slate-400" />
            <span className="text-slate-200 font-medium">{folderData.name}</span>
          </>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search files and folders..."
          className="pl-9"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="files-search"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-400">Loading...</div>
      ) : (
        <div>
          {/* Back button */}
          {currentFolderId && (
            <button onClick={goBack} className="flex items-center gap-2 text-sm text-slate-400 hover:text-indigo-400 mb-4 transition-colors" data-testid="go-back-btn">
              <ArrowLeft size={14} /> Back
            </button>
          )}

          {/* Folders section */}
          {!searchQuery && folders.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Folders</h3>
              <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' : 'space-y-1'}>
                {folders.map((folder) => (
                  <FolderCard
                    key={folder.id} folder={folder} viewMode={viewMode}
                    onOpen={() => openFolder(folder.id)}
                    onRename={() => { setRenameTarget(folder); setRenameName(folder.name); }}
                    onDelete={() => handleDeleteFolder(folder.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Items section */}
          {!searchQuery && items.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Files & Bookmarks</h3>
              <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' : 'space-y-1'}>
                {items.map((item) => (
                  <ItemCard
                    key={item.id} item={item} viewMode={viewMode}
                    Icon={getItemIcon(item)}
                    onDelete={() => handleDeleteItem(item.id)}
                    onDownload={item.item_type === 'file' ? () => handleDownload(item.id) : null}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Filtered results */}
          {searchQuery && filtered && (
            <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' : 'space-y-1'}>
              {filtered.map((item) =>
                item.subfolders !== undefined || (!item.item_type) ? (
                  <FolderCard key={item.id} folder={item} viewMode={viewMode} onOpen={() => openFolder(item.id)} onRename={() => {}} onDelete={() => handleDeleteFolder(item.id)} />
                ) : (
                  <ItemCard key={item.id} item={item} viewMode={viewMode} Icon={getItemIcon(item)} onDelete={() => handleDeleteItem(item.id)} onDownload={item.item_type === 'file' ? () => handleDownload(item.id) : null} />
                )
              )}
            </div>
          )}

          {/* Empty state */}
          {!searchQuery && folders.length === 0 && items.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400" data-testid="empty-state">
              <FolderSimple size={48} weight="thin" className="mb-3" />
              <p className="text-sm font-medium mb-1">{currentFolderId ? 'This folder is empty' : 'No folders yet'}</p>
              <p className="text-xs">Create a folder to start organizing your files</p>
            </div>
          )}
        </div>
      )}

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Folder</DialogTitle></DialogHeader>
          <Input
            autoFocus placeholder="Folder name" value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            data-testid="new-folder-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFolder(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleCreateFolder} data-testid="create-folder-confirm">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={!!renameTarget} onOpenChange={() => setRenameTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rename Folder</DialogTitle></DialogHeader>
          <Input
            autoFocus value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            data-testid="rename-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleRename} data-testid="rename-confirm">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bibliography Dialog */}
      <Dialog open={showBibliography} onOpenChange={setShowBibliography}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardText size={18} className="text-indigo-400" /> Generate Bibliography
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg" data-testid="bib-style-tabs">
            {CITATION_STYLES.map((s) => (
              <button
                key={s.id}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-all ${bibStyle === s.id ? 'bg-slate-800 text-indigo-300 shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
                onClick={() => setBibStyle(s.id)}
                data-testid={`bib-style-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="text-xs text-slate-400 mb-1">
            {items.filter(i => i.item_type === 'bookmark').length} paper(s) in this folder
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-sm text-slate-200 leading-relaxed font-serif overflow-y-auto max-h-[40vh] whitespace-pre-wrap select-all" data-testid="bibliography-text">
            {generateBibliography(
              items.filter(i => i.item_type === 'bookmark').map(i => ({
                ...i,
                title: i.name,
                externalIds: i.external_ids || { DOI: i.url?.includes('doi.org') ? i.url.replace('https://doi.org/', '') : null },
              })),
              bibStyle
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBibliography(false)}>Close</Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700 text-white"
              onClick={() => {
                const bookmarks = items.filter(i => i.item_type === 'bookmark').map(i => ({
                  ...i,
                  title: i.name,
                  externalIds: i.external_ids || { DOI: i.url?.includes('doi.org') ? i.url.replace('https://doi.org/', '') : null },
                }));
                const text = generateBibliography(bookmarks, bibStyle);
                navigator.clipboard.writeText(text);
                toast.success('Bibliography copied to clipboard');
              }}
              data-testid="copy-bibliography-btn"
            >
              <Copy size={14} className="mr-1.5" /> Copy All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FolderCard = ({ folder, viewMode, onOpen, onRename, onDelete }) => {
  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 cursor-pointer group transition-colors" data-testid={`folder-${folder.id}`}>
        <FolderSimple size={20} weight="fill" className="text-amber-500 flex-shrink-0" />
        <span className="flex-1 text-sm text-slate-200 truncate" onClick={onOpen}>{folder.name}</span>
        <span className="text-xs text-slate-400">{formatDate(folder.created_at)}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 rounded transition-all"><DotsThree size={16} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpen}>Open</DropdownMenuItem>
            <DropdownMenuItem onClick={onRename}>Rename</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-red-400">Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }
  return (
    <div className="group relative bg-slate-800 border border-slate-700 rounded-xl p-4 hover:border-teal-300 hover:shadow-md cursor-pointer transition-all" onClick={onOpen} data-testid={`folder-${folder.id}`}>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 hover:bg-slate-100 rounded"><DotsThree size={16} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onRename}>Rename</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-red-400">Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <FolderSimple size={36} weight="fill" className="text-amber-500 mb-2" />
      <p className="text-sm font-medium text-slate-200 truncate">{folder.name}</p>
      <p className="text-xs text-slate-400 mt-0.5">{formatDate(folder.created_at)}</p>
    </div>
  );
};

const ItemCard = ({ item, viewMode, Icon, onDelete, onDownload }) => {
  const itemLabel = item.item_type === 'bookmark' ? 'Paper' : item.item_type === 'link' ? item.entity_type : formatFileSize(item.file_size);
  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 group transition-colors" data-testid={`item-${item.id}`}>
        <Icon size={20} className={item.item_type === 'bookmark' ? 'text-teal-500' : item.item_type === 'link' ? 'text-blue-500' : 'text-slate-400'} />
        <span className="flex-1 text-sm text-slate-200 truncate">
          {item.url ? <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400">{item.name}</a> : item.name}
        </span>
        <span className="text-xs text-slate-400">{itemLabel}</span>
        <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onDownload && <button onClick={onDownload} className="p-1 hover:bg-slate-200 rounded"><DownloadSimple size={14} /></button>}
          <button onClick={onDelete} className="p-1 hover:bg-red-900/20 rounded text-red-500"><Trash size={14} /></button>
        </div>
      </div>
    );
  }
  return (
    <div className="group relative bg-slate-800 border border-slate-700 rounded-xl p-4 hover:border-teal-300 hover:shadow-md transition-all" data-testid={`item-${item.id}`}>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        {onDownload && <button onClick={onDownload} className="p-1 hover:bg-slate-100 rounded"><DownloadSimple size={14} /></button>}
        <button onClick={onDelete} className="p-1 hover:bg-red-900/20 rounded text-red-500"><Trash size={14} /></button>
      </div>
      <Icon size={32} className={`mb-2 ${item.item_type === 'bookmark' ? 'text-teal-500' : item.item_type === 'link' ? 'text-blue-500' : 'text-slate-400'}`} />
      <p className="text-sm font-medium text-slate-200 truncate">
        {item.url ? <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400">{item.name}</a> : item.name}
      </p>
      <p className="text-xs text-slate-400 mt-0.5">{itemLabel}</p>
    </div>
  );
};
